import { useWalletNetwork } from "../../networks/WalletNetworkContext";
import { useCallback, useEffect, useRef, useState } from "react";
import { Buffer } from "buffer";
import { ethers } from "ethers";
import { EVM_CONFIG } from "../../config";
import { evmWalletChainParams } from "../../config/evmNetworks";
import type { BaseStoredNote, BaseWalletState } from "../types";
import type { MutableRefObject } from "react";
import {
  buildBaseDerivationTypedData,
  signBaseDerivation,
} from "../helpers/baseDerivation";
import {
  isPhantomWallet,
  isUserRejection,
  phantomNetworkMessage,
  walletErrorMessage,
  withNumericTypedDataChainId,
} from "../helpers/walletChainSupport";
import {
  loadBaseNotes,
  loadEncryptedBaseNotes,
  preserveBaseWalletCaches,
  saveBaseNotes,
} from "../helpers/storage";
import type { EIP1193Provider } from "./useEIP6963";
import { asDisplayOnlyCachedNotes } from "../helpers/cachedBalance";
import { rememberDogeDerivationFingerprint } from "../helpers/dogeDerivationSafety";
import { deriveSpendingKeys } from "../../joinsplit/keys";
import {
  hasRecoveryKeyOverride,
  hasStoredRecoveryWallet,
  persistRecoveryWallet,
  recoveryPhraseToWalletMaterial,
  resolveRecoveryWallet,
  restoreRecoveryPhrase,
} from "../../joinsplit-v2/recoveryPhrase";
import {
  hasSavedWalletData,
  hasStoredCarryOverWallet,
} from "../../joinsplit-v2/walletCarryOver";
import {
  clearMasterRecoverySession,
  hasStoredMasterRecoverySession,
} from "../../joinsplit-v2/masterRecoveryStorage";
import {
  connectMasterRecoveryWallet,
  type MasterRecoveryBundleLoader,
} from "../../joinsplit-v2/masterRecoveryConnection";
import { configuredEvmRecoveryRegistry } from "../../joinsplit-v2/onchainRecoveryEvm";
import { signMasterVault } from "../helpers/masterVaultSignature";

const TARGET_CHAIN_ID = EVM_CONFIG.CHAIN_ID;
const TARGET_CHAIN_ID_HEX = `0x${TARGET_CHAIN_ID.toString(16)}`;

// The network record carries the wallet_addEthereumChain parameters (Base
// values unchanged; DogeOS Chikyu adds itself to MyDoge/MetaMask by them).
const BASE_CHAIN_PARAMS =
  EVM_CONFIG.NETWORK?.walletChainParams ??
  evmWalletChainParams(TARGET_CHAIN_ID);

interface UseBaseWalletParams {
  addLog: (
    message: string,
    type?: "info" | "success" | "error" | "warning",
  ) => void;
  derivationSignatureB64Ref: MutableRefObject<string>;
  loadMasterRecoveryBundle?: MasterRecoveryBundleLoader;
}

export function useBaseWallet({
  addLog,
  derivationSignatureB64Ref,
  loadMasterRecoveryBundle,
}: UseBaseWalletParams) {
  const acquireNetworkLock = useWalletNetwork()?.acquireLock;
  const generation = useRef(0);
  const lastProvider = useRef<{
    provider: EIP1193Provider;
    name?: string;
  } | null>(null);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );
  // Why the latest connection attempt failed, for the screen; not a dismissal.
  const [connectError, setConnectError] = useState<string | null>(null);
  const [wallet, setWallet] = useState<BaseWalletState>({
    connected: false,
    address: "",
    signer: null,
    spendingKeys: null,
  });
  const [notes, setNotes] = useState<BaseStoredNote[]>([]);
  const [connectedWalletName, setConnectedWalletName] = useState<string | null>(
    null,
  );

  const connectWallet = useCallback(
    async (
      externalProvider?: EIP1193Provider,
      walletName?: string,
      {
        existingWallet = false,
        recoveryPhrase,
        legacyRecoveryPhrase = false,
        linkMasterWallet = false,
      }: {
        /** The user had a private wallet before the signing message changed. */
        existingWallet?: boolean;
        recoveryPhrase?: string;
        legacyRecoveryPhrase?: boolean;
        linkMasterWallet?: boolean;
      } = {},
    ) => {
      const attempt = ++generation.current;
      setConnectError(null);
      derivationSignatureB64Ref.current = "";
      setWallet({
        connected: false,
        address: "",
        signer: null,
        spendingKeys: null,
      });
      setNotes([]);
      const eip1193 = externalProvider ?? (window as any).ethereum;
      if (!eip1193) {
        throw new Error(
          "No wallet provider available. Install a compatible EVM wallet.",
        );
      }
      const releaseNetwork = acquireNetworkLock?.();
      let removeConnectionListeners: (() => void) | undefined;
      try {
        // Validate locally before requesting a wallet signature. Never forward
        // the phrase (or parsing errors containing it) to a provider or server.
        if (recoveryPhrase !== undefined)
          recoveryPhraseToWalletMaterial(recoveryPhrase);
        addLog(
          `Connecting${walletName ? ` via ${walletName}` : ""}...`,
          "info",
        );

        await eip1193.request({ method: "eth_requestAccounts" });

        try {
          await eip1193.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: TARGET_CHAIN_ID_HEX }],
          });
        } catch (switchErr: any) {
          try {
            if (switchErr?.code !== 4902) throw switchErr;
            await eip1193.request({
              method: "wallet_addEthereumChain",
              params: [BASE_CHAIN_PARAMS],
            });
          } catch (networkErr) {
            if (
              isPhantomWallet(eip1193, walletName) &&
              !isUserRejection(networkErr)
            )
              throw new Error(
                phantomNetworkMessage(
                  TARGET_CHAIN_ID,
                  EVM_CONFIG.NETWORK?.label ?? "this network",
                ),
              );
            throw networkErr;
          }
        }

        const provider = new ethers.BrowserProvider(
          isPhantomWallet(eip1193, walletName)
            ? withNumericTypedDataChainId(eip1193)
            : eip1193,
        );
        const signer = await provider.getSigner();
        const address = await signer.getAddress();
        const target = {
          scope: `evm:${TARGET_CHAIN_ID}:${(EVM_CONFIG.DERIVATION_POOL || EVM_CONFIG.CONTRACTS.SHIELDED_POOL_NOVA).toLowerCase()}`,
          wallet: address.toLowerCase(),
        };
        const legacyPhrase =
          recoveryPhrase !== undefined &&
          (legacyRecoveryPhrase ||
            !recoveryPhraseToWalletMaterial(recoveryPhrase).recoveryEntropy);
        const registry = configuredEvmRecoveryRegistry(TARGET_CHAIN_ID);
        const useMasterWallet = !!registry && !legacyPhrase;
        const hasLegacyWallet =
          !useMasterWallet &&
          recoveryPhrase === undefined &&
          (existingWallet ||
            hasStoredRecoveryWallet(target) ||
            hasStoredCarryOverWallet(target) ||
            hasSavedWalletData((key) => {
              const lower = key.toLowerCase();
              return (
                lower.includes(target.wallet) &&
                (lower.includes(`_${TARGET_CHAIN_ID}_`) ||
                  lower.includes(`:${TARGET_CHAIN_ID}:`))
              );
            }));
        const masterTarget = registry
          ? {
              scope: `master-v1:evm:${TARGET_CHAIN_ID}:${registry.address.toLowerCase()}`,
              wallet: target.wallet,
            }
          : target;
        const hasMasterWallet =
          !!registry && hasStoredMasterRecoverySession(masterTarget);
        const create12WordWallet =
          !useMasterWallet &&
          !existingWallet &&
          recoveryPhrase === undefined &&
          !hasRecoveryKeyOverride(target) &&
          !hasLegacyWallet;
        const useRecoveryKey =
          recoveryPhrase !== undefined ||
          useMasterWallet ||
          hasMasterWallet ||
          hasRecoveryKeyOverride(target);
        // Events invalidate a restore even if the account changes during crypto
        // work after the final RPC check. Do not save a vault for that session.
        let connectionChanged = false;
        const invalidate = () => {
          connectionChanged = true;
        };
        const emitter = eip1193 as any;
        if (
          typeof emitter.on === "function" &&
          typeof emitter.removeListener === "function"
        ) {
          emitter.on("accountsChanged", invalidate);
          emitter.on("chainChanged", invalidate);
          removeConnectionListeners = () => {
            emitter.removeListener("accountsChanged", invalidate);
            emitter.removeListener("chainChanged", invalidate);
          };
        }
        const isCurrent = () =>
          attempt === generation.current && !connectionChanged;

        addLog("Creating your private wallet...", "info");
        // Wallet access and phrase restoration must not depend on the hosted
        // authentication service. This is the same locally pinned preimage
        // signBaseDerivation already validates; service login happens later.
        const derivationTypedData = buildBaseDerivationTypedData(address);
        // `signatureHex` seeds the keys; `newSignatureHex` signs the current
        // preimage, which is what the DogeOS repeatability check remembers.
        // Nothing is saved on the device before the connection is verified.
        const finish = async ({
          signatureHex,
          typedData,
          newSignatureHex,
          commit,
        }: Awaited<ReturnType<typeof signBaseDerivation>>) => {
          const signatureB64 = Buffer.from(
            ethers.getBytes(signatureHex),
          ).toString("base64");
          const [candidateDigest, encryptedCached] = await Promise.all([
            crypto.subtle.digest(
              "SHA-256",
              new Uint8Array(ethers.getBytes(signatureHex)).buffer,
            ),
            useRecoveryKey
              ? Promise.resolve([])
              : loadEncryptedBaseNotes(address, signatureB64),
          ]);
          const legacyMaterial = !useMasterWallet
            ? recoveryPhrase !== undefined
              ? recoveryPhraseToWalletMaterial(recoveryPhrase)
              : await resolveRecoveryWallet(
                  target,
                  ethers.getBytes(newSignatureHex),
                  new Uint8Array(candidateDigest),
                  { isCurrent, create12WordWallet },
                )
            : undefined;
          const [chain, accounts] = await Promise.all([
            eip1193.request({ method: "eth_chainId" }),
            eip1193.request({ method: "eth_accounts" }),
          ]);
          if (!isCurrent())
            throw new Error("Wallet connection was cancelled or replaced.");
          if (
            BigInt(String(chain)) !== BigInt(TARGET_CHAIN_ID) ||
            !Array.isArray(accounts) ||
            String(accounts[0]).toLowerCase() !== address.toLowerCase()
          )
            throw new Error(
              "Wallet account or network changed. Reconnect before continuing.",
            );
          const master = useMasterWallet
            ? await connectMasterRecoveryWallet({
                target: masterTarget,
                signature: ethers.getBytes(newSignatureHex),
                network: {
                  chain: "evm",
                  chainDomain: ethers.toBeHex(TARGET_CHAIN_ID, 32),
                },
                phrase: recoveryPhrase,
                link: linkMasterWallet,
                isCurrent,
                loadBundle: loadMasterRecoveryBundle,
                confirmReplacement: async () => {
                  if (
                    !window.confirm(
                      "This phrase opens a different StealthOS wallet. Save the current recovery phrase before switching. Existing encrypted backups will be kept. Switch wallets?",
                    )
                  )
                    return false;
                  await preserveBaseWalletCaches(address);
                  return isCurrent();
                },
              })
            : null;
          const material = master?.material ?? legacyMaterial!;
          const spendingKeys = {
            ...(await deriveSpendingKeys(material.spendingKey)),
            ...(material.recoveryEntropy
              ? { recoveryEntropy: material.recoveryEntropy }
              : {}),
            ...(master ? { masterRecovery: master.masterRecovery } : {}),
          };
          if (!isCurrent())
            throw new Error("Wallet connection was cancelled or replaced.");
          if (create12WordWallet)
            await persistRecoveryWallet(
              target,
              ethers.getBytes(newSignatureHex),
              material,
              { isCurrent },
            );
          if (!master && recoveryPhrase !== undefined)
            await restoreRecoveryPhrase(
              target,
              ethers.getBytes(newSignatureHex),
              recoveryPhrase,
              {
                isCurrent,
                allowCorruptRecordReplacement: true,
                confirmIdentityReplacement: async () => {
                  const accepted = window.confirm(
                    "This phrase opens a different private wallet. Save the current wallet's StealthOS recovery phrase first if you still need it. Switch private wallets? Existing encrypted note backups will be kept.",
                  );
                  if (!accepted || !isCurrent()) return false;
                  await preserveBaseWalletCaches(address);
                  return isCurrent();
                },
              },
            );
          if (!isCurrent())
            throw new Error("Wallet connection was cancelled or replaced.");
          if (!master && TARGET_CHAIN_ID === 6281971 && !useRecoveryKey)
            rememberDogeDerivationFingerprint(typedData, newSignatureHex);
          await commit();
          if (!isCurrent())
            throw new Error("Wallet connection was cancelled or replaced.");
          if (!master)
            await persistRecoveryWallet(
              target,
              ethers.getBytes(newSignatureHex),
              material,
              { isCurrent },
            );
          if (!isCurrent())
            throw new Error("Wallet connection was cancelled or replaced.");
          const cachedDisplayNotes = asDisplayOnlyCachedNotes([
            ...(!master ? loadBaseNotes(address) : []),
            // A restore can explicitly select another private identity. Never
            // display notes from the prior signature-scoped cache as its funds.
            ...(!master && !useRecoveryKey ? encryptedCached : []),
          ]);
          derivationSignatureB64Ref.current = signatureB64;
          setWallet({ connected: true, address, signer, spendingKeys });
          lastProvider.current = { provider: eip1193, name: walletName };
          setConnectedWalletName(walletName ?? null);
          setNotes(cachedDisplayNotes);
          if (!master) saveBaseNotes(address, cachedDisplayNotes);
          addLog(
            `${recoveryPhrase !== undefined ? "Private wallet restored; connected" : "Connected"} ${address.slice(0, 10)}...${walletName ? ` (${walletName})` : ""}`,
            "success",
          );
        };
        const derived = useMasterWallet
          ? await signMasterVault(signer, address, isCurrent)
          : await signBaseDerivation(signer, derivationTypedData, address, {
              existingWallet,
              useRecoveryKey,
              isCurrent,
              onPreviousSignature: () =>
                addLog(
                  "One more signature brings your existing private wallet to this device. You only do this once here.",
                  "info",
                ),
            });
        if (attempt !== generation.current) return;
        await finish(derived);
      } catch (error) {
        if (attempt === generation.current) {
          const message = walletErrorMessage(error);
          addLog(message, "error");
          if (!isUserRejection(error)) setConnectError(message);
        }
      } finally {
        removeConnectionListeners?.();
        releaseNetwork?.();
      }
    },
    [
      acquireNetworkLock,
      addLog,
      wallet.address,
      derivationSignatureB64Ref,
      loadMasterRecoveryBundle,
    ],
  );

  const disconnectWallet = useCallback(() => {
    generation.current += 1;
    clearMasterRecoverySession();
    lastProvider.current = null;
    derivationSignatureB64Ref.current = "";
    setWallet({
      connected: false,
      address: "",
      signer: null,
      spendingKeys: null,
    });
    setNotes([]);
    setConnectedWalletName(null);
    addLog("Wallet disconnected", "info");
  }, [addLog, derivationSignatureB64Ref]);

  const linkMasterWallet = useCallback(async () => {
    const current = lastProvider.current;
    if (!current)
      throw new Error(
        "Connect the chain wallet before linking its recovery phrase.",
      );
    await connectWallet(current.provider, current.name, {
      linkMasterWallet: true,
      existingWallet: true,
    });
  }, [connectWallet]);

  const updateNotes = useCallback(
    (updater: (prev: BaseStoredNote[]) => BaseStoredNote[]) => {
      setNotes((prev) => {
        const next = updater(prev);
        if (wallet.address) saveBaseNotes(wallet.address, next);
        return next;
      });
    },
    [wallet.address],
  );

  return {
    wallet,
    notes,
    setNotes,
    updateNotes,
    connectWallet,
    disconnectWallet,
    linkMasterWallet,
    connectedWalletName,
    connectError,
  };
}
